import { useEffect, useRef } from 'react';
import useLang from 'hooks/useLang';
import styled from 'styled-components';

const FOCUSABLE_SELECTOR = 'button:not([disabled])';

// DEV-1401: purpose-built local overlay for genuine PayTo backend/transport
// failures, cloning the existing SystemErrorModal look without going through
// it or the shared single-slot PortalModal -- showing SYSTEM_ERROR_MODAL via
// the global portal would unmount whichever PayTo modal is currently open
// (PortalModal/index.jsx resolves exactly one component from namePortalModal),
// destroying the user's in-progress form. This component is rendered directly
// in the PayTo popup's own tree, driven by local state, so it can visually
// stack above the still-open PayTo modal without touching it.
//
// Deliberately does NOT reuse AlertPopup/ButtonCommon's isUseKeyDown wiring:
// AlertPopup's Close button hardcodes isUseKeyDown, which registers a
// document-level keydown listener via useEnterKeyDown -- that listener is
// gated only by isLoading/isDesktop, never by a button's own disabled state,
// so it can co-fire with the underlying PayTo modal's own still-mounted
// Enter-to-submit listener. This component uses a plain native button and its
// own Escape-only handling instead.
const PaytoSystemErrorOverlay = ({ isShow, title, content, onClose }) => {
  const { t } = useLang();
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  useEffect(() => {
    if (!isShow) return undefined;

    previousActiveElementRef.current = document.activeElement;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll(
          FOCUSABLE_SELECTOR,
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        // Single-button dialog today: first === last, so either branch just
        // re-focuses the same element -- harmless, and keeps this correct if
        // a second focusable element is ever added later.
        if (document.activeElement === last && !e.shiftKey) {
          e.preventDefault();
          first.focus();
        } else if (document.activeElement === first && e.shiftKey) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const saved = previousActiveElementRef.current;
      if (saved && document.body.contains(saved)) {
        saved.focus();
      }
    };
  }, [isShow, onClose]);

  if (!isShow) return null;

  return (
    <OverlayStyled>
      <DialogStyled ref={dialogRef} role="alertdialog" aria-modal="true">
        {!!title && <Title>{t(title)}</Title>}
        {!!content && <Content>{t(content)}</Content>}
        <CloseButtonStyled ref={closeButtonRef} type="button" onClick={onClose}>
          {t('button_close')}
        </CloseButtonStyled>
      </DialogStyled>
    </OverlayStyled>
  );
};

// DEV-1401: z-index derived from a fresh full-tree grep of this app's actual
// ceiling, not assumed -- highest confirmed elsewhere is 10001
// (LoadingScreen/ver2/index.jsx), with the mobile header/mask at 1065/1066.
// 10010 clears both. Uses an rgba background (not `opacity`) so the dialog
// child renders at full opacity rather than inheriting the backdrop's dim.
const OverlayStyled = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10010;
  background: rgba(33, 33, 33, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const DialogStyled = styled.div`
  width: 100%;
  max-width: 343px;
  padding: 16px;
  border-radius: 8px;
  box-shadow: var(--ds-bs-4);
  background: var(--ds-c-white);
`;

const Title = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: var(--ds-c-blue);
  margin: 0 0 8px;
`;

const Content = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  white-space: break-spaces;
  color: var(--ds-c-grey-dark);
  margin: 0 0 16px;
`;

const CloseButtonStyled = styled.button`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 44px;
  border: none;
  outline: none;
  border-radius: 100px;
  padding: 0 10px;
  margin: 0;
  color: var(--c-primary);
  background: var(--bg-primary);
  cursor: pointer;

  &:focus,
  &:focus-visible,
  &:active {
    color: var(--c-primary);
    background: var(--bg-primary);
    outline: none;
  }
`;

export default PaytoSystemErrorOverlay;
